import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Formik, FormikHelpers, Form } from 'formik';
import { DateTime } from 'luxon';

import MenuItem from '@material-ui/core/MenuItem';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import createStyles from '@material-ui/core/styles/createStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import Container from '@material-ui/core/Container';
import EditIcon from '@material-ui/icons/EditOutlined';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { Typography, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress } from '@material-ui/core';
import FormTextField from 'components/FormFields/FormTextField';
import FormSelectField from 'components/FormFields/FormSelectField';
import DatePicker from 'components/FormFields/FormDatePicker';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import InfoIcon from '@material-ui/icons/Info';
import ErrorIcon from '@material-ui/icons/Error';

import Tooltip from 'components/Tooltip/Tooltip';
import { realms } from 'helpers/realms';
import FormCheckboxGroup from 'components/FormCheckboxGroup/FormCheckboxGroup';
import Switch from 'components/FormFields/Switch';
import { isoDateTimeToLabel } from 'helpers/helpers';
import Background from 'components/Layout/Background';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import { MessageData } from 'components/Layout/Notification';

import { RootStore } from 'stores/rootStore';
import { BackofficeNotificationsStore } from 'stores/admin/backofficeNotifications';
import { EdwardColors } from 'enums';

interface Props {
  rootStore: RootStore;
}

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& .MuiTableCell-root': {
        padding: theme.spacing(0, 0.5),
        verticalAlign: 'middle',
        '&:first-child svg:not(.MuiCircularProgress-svg)': {
          marginRight: '8px',
          verticalAlign: 'middle',
        },
      },
    },
    loader: {
      textAlign: 'center',
      padding: `${theme.spacing(4)}px !important`,
    },

    checks: {
      '& .MuiFormGroup-root': {
        flexDirection: 'row',
      },
      '& .MuiFormControlLabel-root': {
        width: '33%',
        marginRight: 0,
      },
    },
    select: {
      '& .MuiSelect-select svg': {
        verticalAlign: 'middle',
      },
    },
  }),
);

const initialNotification = {
  title: '',
  message: '',
  severity: 'NORMAL' as 'NORMAL' | 'CRITICAL', // seriously, you're gotta be fuckin' kiddin' me
  realms: [''],
  id: '',
  visible: true,
  validFrom: '',
  validTo: '',
};

const Notifications: React.FC<Props> = ({ rootStore }) => {
  const { t } = useTranslation();
  const [store] = useState(new BackofficeNotificationsStore(rootStore));
  const { notifications, fetchNotifications, editOpen } = store;
  const classes = useStyles();
  const [editNotification, setEditNotification] = useState<MessageData | null>(null);
  const [deleteId, setDeleteId] = useState<string | undefined>(undefined);
  const realmOptions = [...new Set(Object.values(realms))];
  initialNotification.realms = realmOptions.map((r) => r.toUpperCase());

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications, store]);

  const submit = (values: MessageData, formikHelpers: FormikHelpers<MessageData>) => {
    store.handleSubmit(values, formikHelpers);
  };

  return (
    <Container maxWidth="md">
      <Background inside>
        <Typography variant="h1">{t('backOffice_notifications')}</Typography>

        <Table className={classes.root}>
          <TableHead>
            <TableRow>
              <TableCell>{t('backOffice_notifications_notification')}</TableCell>
              <TableCell>{t('general_since')}</TableCell>
              <TableCell>{t('general_until')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {notifications && (
              <>
                {notifications.length ? (
                  notifications.map((note) => (
                    <TableRow key={note.id}>
                      <TableCell>
                        {note.severity === 'NORMAL' ? (
                          <InfoIcon htmlColor={EdwardColors.DARK} />
                        ) : (
                          <ErrorIcon htmlColor={EdwardColors.ALERT} />
                        )}
                        {note.title && <strong>{note.title}:&nbsp;</strong>}
                        {note.message}
                      </TableCell>
                      <TableCell>{isoDateTimeToLabel(note.validFrom)}</TableCell>
                      <TableCell>{isoDateTimeToLabel(note.validTo)}</TableCell>
                      <TableCell>
                        <Tooltip title={t(note.visible ? 'general_hide' : 'general_show')}>
                          <span>
                            <IconButton
                              size="medium"
                              onClick={() => store.handleSubmit({ ...note, visible: !note.visible })}
                            >
                              {note.visible ? (
                                <Visibility fontSize="medium" htmlColor={EdwardColors.GRAY_MEDIUM} />
                              ) : (
                                <VisibilityOff fontSize="medium" htmlColor={EdwardColors.GRAY_LIGHT} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <IconButton
                          size="medium"
                          onClick={() => {
                            setEditNotification(note);
                            store.setEditOpen(true);
                          }}
                        >
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton size="medium" onClick={() => setDeleteId(note.id)}>
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className={classes.loader}>
                      {t('backOffice_notifications_empty')}
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
            {!notifications && (
              <TableRow>
                <TableCell colSpan={4} className={classes.loader}>
                  <CircularProgress size={40} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
          <Button
            onClick={() => {
              setEditNotification(null);
              store.setEditOpen(true);
            }}
            variant="contained"
            color="primary"
          >
            {t(`backOffice_notifications_new`)}
          </Button>
        </div>
      </Background>

      <ConfirmDialog
        noHeader
        isOpen={deleteId !== undefined}
        confirm={async () => {
          deleteId && (await store.deleteNotification(deleteId));
          setDeleteId(undefined);
        }}
        close={() => setDeleteId(undefined)}
      >
        {t('backOffice_notifications_confirmDelete')}
      </ConfirmDialog>

      <Formik initialValues={editNotification || initialNotification} onSubmit={submit} enableReinitialize>
        {(formikProps) => (
          <Dialog open={editOpen} maxWidth="md">
            <DialogContent>
              <FormWithoutEnterSubmit id="admin_notification">
                <Grid container>
                  <Grid item xs={12}>
                    <FormTextField name="title" label={t(`backOffice_notifications_title`)} fast={false} />
                  </Grid>
                  <Grid item xs={12}>
                    <FormTextField
                      name="message"
                      multiline={true}
                      minRows={4}
                      maxLength={500}
                      label={t(`backOffice_notifications_message`)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DatePicker
                      minDate={DateTime.local()}
                      name={`validFrom`}
                      label={t('backOffice_notifications_validFrom')}
                      format="dd.MM.yyyy HH:mm"
                      time
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DatePicker
                      minDate={DateTime.local()}
                      name={`validTo`}
                      label={t('general_validTo')}
                      format="dd.MM.yyyy HH:mm"
                      time
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormSelectField
                      name="severity"
                      label={t('general_type')}
                      className={classes.select}
                    >
                      <MenuItem value="NORMAL">
                        <InfoIcon htmlColor={EdwardColors.DARK} />
                        &nbsp;
                        {t(`backOffice_notifications_severity_NORMAL`)}
                      </MenuItem>
                      <MenuItem value="CRITICAL">
                        <ErrorIcon htmlColor={EdwardColors.ALERT} />
                        &nbsp;
                        {t(`backOffice_notifications_severity_CRITICAL`)}
                      </MenuItem>
                    </FormSelectField>
                  </Grid>
                  <Grid item xs={6}>
                    <Switch name={`visible`} label={t(`backOffice_notifications_visible`)} />
                  </Grid>
                  <Grid item xs={12}>
                    <FormCheckboxGroup
                      name={`realms`}
                      label={t('backOffice_notifications_realms')}
                      values={formikProps.values['realms']?.map(r => r.toUpperCase()) as string[]}
                      options={realmOptions.map((value) => value.toUpperCase())}
                      optPrefix="intermediary_"
                      optSuffix="_short"
                      className={classes.checks}
                    />
                  </Grid>
                </Grid>
              </FormWithoutEnterSubmit>
            </DialogContent>
            <DialogActions>
              <Button type="submit" variant="contained" color="primary" form="admin_notification">
                {t('general_ok')}
              </Button>
              <Button variant="contained" onClick={() => store.setEditOpen(false)}>
                {t('general_cancel')}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Formik>
    </Container>
  );
};

export default compose(inject('rootStore'), withRouter, observer)(Notifications);
